<template>
  <div>
    <LoadingComponent v-if="universityStore.isLoading" class="loader" />
    <div v-else>
      <div v-if="selectedUniversity">
        <div class="hero-section">
          <div
            class="container row justify-center"
            :class="$q.screen.lt.md ? 'q-gutter-y-lg' : 'q-pa-none'"
          >
            <div class="col-12 col-md-5 text-white q-gutter-md flex flex-center">
              <div class="column">
                <div class="yellow-bottom-card bg-white uni-logo flex flex-center">
                  <img
                    :src="getUniPhoto(selectedUniversity)"
                    :alt="getUniName(selectedUniversity)"
                  />
                </div>
              </div>
            </div>
            <div
              class="col-12 col-md-6 text-white q-gutter-md"
              :class="$q.screen.lt.md ? 'text-center' : 'text-left'"
            >
              <h1 class="text-h4 text-bold">{{ getUniName(selectedUniversity) }}</h1>
              <div
                class="text-justify"
                v-if="selectedUniversity.description_en"
                v-html="getUniDescription(selectedUniversity)"
              ></div>
              <q-btn
                rounded
                color="secondary"
                text-color="primary"
                @click="openURL(selectedUniversity.website)"
                icon="language"
                no-caps
              >
                {{ $t('institution_details.visit_btn') }}
              </q-btn>
            </div>
          </div>
        </div>
        <div class="grey-background">
          <div class="container section">
            <div class="header">
              <span class="title">{{ $t('institution_details.programs') }}</span>
            </div>
            <div class="q-gutter-lg">
              <router-link
                v-for="programme in programmes"
                :key="programme.id"
                class="programme-card rounded-borders with-shadow cursor-pointer card-link"
                :to="getRoutePath(programme)"
              >
                <div class="row justify-center q-col-gutter-md">
                  <div
                    class="col-10 col-md-4 flex"
                    :class="
                      $q.screen.lt.md ? 'justify-center text-center' : 'items-center justify-start'
                    "
                  >
                    <span class="text-h6">{{ getName(programme) }}</span>
                  </div>
                  <div
                    class="col-10 col-md-6 flex items-center justify-start"
                    :class="$q.screen.lt.md ? 'justify-center' : 'justify-start'"
                  >
                    <q-chip square color="info" text-color="primary" icon="book">
                      {{ getType(programme) }}
                    </q-chip>
                    <q-chip square icon="local_library" color="info" text-color="primary">
                      {{ getStudyModes(programme).join('/') }}
                    </q-chip>
                    <q-chip
                      v-if="programme.languages.includes('English')"
                      icon="language"
                      color="info"
                      text-color="primary"
                      square
                    >
                      {{ $t('programs.lang_label') }}
                    </q-chip>
                    <div v-if="$q.screen.lt.md" class="flex justify-center">
                      <q-chip
                        v-for="topic in getTopics(programme)"
                        :key="topic"
                        square
                        class="ellipsis"
                        icon="topic"
                        color="info"
                        text-color="primary"
                      >
                        {{ topic }}
                      </q-chip>
                    </div>
                    <q-chip
                      v-else
                      square
                      class="ellipsis"
                      size="12px"
                      color="info"
                      text-color="primary"
                      icon="topic"
                    >
                      {{ getTopics(programme).join('/') }}
                    </q-chip>
                  </div>
                  <div class="col-10 col-md-2 flex flex-center text-h6">
                    {{ getListFees(programme) }}
                  </div>
                </div>
              </router-link>
            </div>
            <div class="row q-pt-lg justify-center">
              <q-pagination
                :model-value="params.pagination.page"
                :max="Math.ceil(programmeStore.total / params.pagination.limit)"
                :max-pages="6"
                direction-links
                boundary-links
                @update:model-value="setPage"
                color="primary"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { openURL } from 'quasar'
import { useI18n } from 'vue-i18n'

import { useUniversityStore } from 'stores/university-store'
import { useProgrammeStore } from 'stores/programme-store'

import { useProgrammeLocale } from 'src/composables/programme/useProgrammeLocale'
import { useProgrammeApiQuery } from 'src/composables/programme/useProgrammeApiQuery'
import { useProgrammeQuery } from 'src/composables/programme/useProgrammeQuery'
import { useUniversityLocale } from 'src/composables/university/useUniversityLocale'
import { useProgrammeRoute } from 'src/composables/programme/useProgrammeRoute'
import { useSeoMeta } from 'src/composables/useSeoMeta'

import LoadingComponent from 'components/main/LoadingComponent.vue'

defineOptions({
  async preFetch({ store, ssrContext, currentRoute }) {
    const universityStore = useUniversityStore(store)
    const programmeStore = useProgrammeStore(store)

    const id = Number(currentRoute.params.id)
    const { pagination, filters } = useProgrammeQuery(currentRoute)

    filters.university_id = [id]
    await Promise.all([
      universityStore.initUniversity(id, ssrContext),
      programmeStore.initProgrammes(pagination, filters, ssrContext),
    ])
  },
})

const { t } = useI18n()
const universityStore = useUniversityStore()
const programmeStore = useProgrammeStore()

const { params, setPage } = useProgrammeApiQuery()

const { programmes } = storeToRefs(programmeStore)
const { selectedUniversity } = storeToRefs(universityStore)

const { getUniPhoto, getUniName, getUniDescription } = useUniversityLocale()

const { getName, getListFees, getType, getTopics, getStudyModes } = useProgrammeLocale()

const { getRoutePath } = useProgrammeRoute()

useSeoMeta({
  title: () => (selectedUniversity.value ? getUniName(selectedUniversity.value) : 'Loading...'),

  description: () => {
    if (!selectedUniversity.value) return ''
    return getUniDescription(selectedUniversity.value)?.substring(0, 160) || ''
  },

  image: () => (selectedUniversity.value ? getUniPhoto(selectedUniversity.value) : undefined),

  breadcrumbs: () => [
    { name: t('navbar.home'), url: '/' },
    { name: t('institutions.title'), url: '/universities' },
    {
      name: selectedUniversity.value ? getUniName(selectedUniversity.value) : '...',
      url: `/universities/${selectedUniversity.value?.id}/details`,
    },
  ],

  schema: () => {
    if (!selectedUniversity.value) return { type: 'WebPage' }
    const name = getUniName(selectedUniversity.value)
    return {
      type: 'CollegeOrUniversity',
      data: {
        name: name,
        description: getUniDescription(selectedUniversity.value),
        image: getUniPhoto(selectedUniversity.value),
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: `Programs offered by ${name}`,
          itemListElement: programmes.value.map((prog) => ({
            '@type': 'ListItem',
            item: {
              '@type': 'EducationalOccupationalProgram',
              name: getName(prog),
              url: getRoutePath(prog),
              provider: {
                '@type': 'CollegeOrUniversity',
                name: name,
              },
            },
          })),
        },
      },
    }
  },
})
</script>
<style scoped lang="scss">
.programme-card {
  padding: 30px;
  background-color: white;

  &:hover {
    border-style: solid;
    border-color: $secondary;
  }
}

.uni-logo {
  padding: 40px;
  height: 300px;
  width: 350px;

  img {
    width: 100%;
  }

  @media (max-width: 768px) {
    padding: 10px;
    width: 240px;
    height: 240px;

    img {
      width: 100%;
    }
  }
}
</style>
